import { useContext, useEffect, useId, useLayoutEffect, useRef, useState, type CSSProperties } from 'react';
import { createPortal } from 'react-dom';
import { Color, Size, t, useSplitButton, type Consequence, type DestructiveSubject } from '@bms2/shared';
import { Icon } from '../Icon';
import { ConfirmationDialog } from './ConfirmationDialog';
import { DestructiveConfirmationDialog } from './DestructiveConfirmationDialog';
import { SplitAsButtonsContext } from './splitAsButtons';
import { sizeClassMap } from '../classMaps';
import styles from './Button.module.css';

/**
 * Button (piece 3): one construction over the native button taking size,
 * color, text[] and actions[]. More than one entry makes it a split button
 * with two click zones, whose open panel re-arms the main zone and fires
 * nothing. A Button with color Destructive routes its action through the
 * Destructive Action Confirmation Dialog itself (decision 0093): the caller
 * hands over the action and never opens the dialog.
 */
interface ButtonBase {
  size?: Size;
  text: readonly string[];
  actions: readonly (() => void)[];
  disabled?: boolean;
  /**
   * 🔒 Why this button cannot be pressed, in words a person reads by resting on
   * it (decision 0559). A button that is disabled and carries no action at all
   * states it: a control that neither acts nor explains itself cannot be told
   * apart from a broken one, and the button check refuses one that does
   * neither.
   */
  disabledReason?: string;
  type?: 'button' | 'submit';
  /**
   * 🔨 A button that is one of a set the person is reading one member of, such as a
   * list's queue bar, says whether it is the member being read: `aria-pressed` on
   * its pressable control, true or false. Left out, the button states nothing.
   */
  pressed?: boolean;
  /**
   * 🔨 A button that opens and closes a list of its own, such as a phone footer's More (decision 3052), says
   * whether the list stands open: `aria-expanded` on its pressable control. Left out, the button states nothing.
   */
  expanded?: boolean;
  /**
   * 🔨 The id the pressable control itself carries, for a button that opens a
   * modal: the shell returns focus to the element this names when the layer
   * closes (piece 5). 💰 Four pages opened the sheet viewer through this control
   * and none could be returned to, because there was no way to name it (found in
   * review, 2026-09-19).
   */
  id?: string;
}

/**
 * 🔨 A Destructive-coloured button always confirms, and it says which way: a
 * typed phrase for what cannot be undone, and the ordinary Confirmation dialog
 * for what can (decision 0262, qualifying decision 0093). 💰 Asking for a typed
 * DELETE in front of a reversible action teaches the reflex that defeats the
 * gate where it matters.
 */
export type ButtonProps =
  | (ButtonBase & { color?: Exclude<Color, Color.Destructive>; destructive?: undefined; confirm?: undefined })
  | (ButtonBase & { color: Color.Destructive; destructive: DestructiveSubject; confirm?: undefined })
  | (ButtonBase & { color: Color.Destructive; destructive?: undefined; confirm: ReversibleConfirm });

/** A reversible destructive action: its title, what it will do, and the word on the confirming button. */
export interface ReversibleConfirm {
  title: string;
  consequence: Consequence;
  confirmLabel: string;
}

/** The harness's named case: colour × size, each colour disabled, and the split construction. */
export const buttonDocumentedStates = [
  ...Object.values(Color).flatMap((c) => Object.values(Size).map((s) => `${c}/${s}`)),
  ...Object.values(Color).map((c) => `${c}/disabled`),
  'split',
] as const;

const sizeClass = sizeClassMap({ small: styles.small!, large: styles.large! });
const colorClass: Record<Color, string> = {
  [Color.Navy]: '',
  [Color.Destructive]: styles.destructive!,
  [Color.Good]: styles.good!,
  [Color.Warning]: styles.warning!,
  [Color.Error]: styles.error!,
  [Color.Inactive]: styles.inactive!,
};

/**
 * Where the open panel is drawn, and in whose coordinates. 🔨 The panel is
 * never a child of the button: an ancestor with its own scroll box clips it,
 * and the region scrolls instead of the menu opening (product owner,
 * 2026-09-14). It goes into the top-layer element the button is in — the open
 * `dialog` where there is one, the page body otherwise — exactly as the
 * Dropdown's list does, and for the same reason: a layer placed on the body
 * from inside a modal is painted behind it and shows nothing.
 */
interface PanelPlacement {
  top: number;
  left: number;
  width: number;
  layer: HTMLElement;
  /**
   * On the page the panel is placed in the document and is carried by the same
   * scroll as the button, so no script runs; against a dialog, which is fixed
   * to the viewport, the panel is fixed too.
   */
  scheme: 'document' | 'viewport';
  /** Which side of the button the panel opens on; below unless the viewport has no room for it there. */
  direction: 'below' | 'above';
}

/** The panel keeps this far from the viewport's edge, and flips above the button rather than run past the bottom of it. */
const PANEL_EDGE_GAP = 8;

export function Button(props: ButtonProps) {
  const { size = Size.Medium, text, actions, disabled = false, disabledReason, type = 'button', id, pressed, expanded } = props;
  const color = props.color ?? Color.Navy;
  const split = useSplitButton({ text, actions, disabled });
  const [dialogOpen, setDialogOpen] = useState(false);
  const wrapper = useRef<HTMLSpanElement>(null);
  const panel = useRef<HTMLUListElement>(null);
  const [placement, setPlacement] = useState<PanelPlacement | null>(null);
  const reasonId = useId();
  /** A disabled button that says why says it in both places: the pointer's title and the reader's description. */
  const showsReason = disabled && disabledReason !== undefined && disabledReason !== '';

  // 🔨 The panel lives outside the wrapper now, so a pointer landing inside it
  // is not an outside click; closing on one would unmount the entry before its
  // own click could fire, and choosing an option would do nothing.
  useEffect(() => {
    if (!split.open) return;
    const onPointerDown = (e: PointerEvent) => {
      const target = e.target as Node;
      const inside = (wrapper.current?.contains(target) ?? false) || (panel.current?.contains(target) ?? false);
      if (!inside) split.close();
    };
    document.addEventListener('pointerdown', onPointerDown);
    return () => document.removeEventListener('pointerdown', onPointerDown);
  }, [split]);

  // The panel is placed under its button and follows it through scroll and resize while open.
  useLayoutEffect(() => {
    // A closed panel renders nothing, so the last placement is simply left where it was; the next open re-measures before paint.
    if (!split.open) return undefined;
    const place = () => {
      const el = wrapper.current;
      if (!el) return;
      const r = el.getBoundingClientRect();
      const dialog = el.closest('dialog');
      const scheme: 'document' | 'viewport' = dialog ? 'viewport' : 'document';
      // A panel that would run past the bottom of the viewport opens upward instead of being cut off there.
      const height = panel.current?.offsetHeight ?? 0;
      const room = typeof window === 'undefined' ? 0 : window.innerHeight - r.bottom - PANEL_EDGE_GAP;
      const above = height > 0 && room < height && r.top > height + PANEL_EDGE_GAP;
      const top = above ? r.top - height : r.bottom;
      setPlacement({
        top: scheme === 'document' ? top + window.scrollY : top,
        left: scheme === 'document' ? r.left + window.scrollX : r.left,
        width: r.width,
        layer: dialog ?? document.body,
        scheme,
        direction: above ? 'above' : 'below',
      });
    };
    place();
    const viewport = window.visualViewport ?? null;
    window.addEventListener('scroll', place, true);
    window.addEventListener('resize', place);
    viewport?.addEventListener('scroll', place);
    viewport?.addEventListener('resize', place);
    return () => {
      window.removeEventListener('scroll', place, true);
      window.removeEventListener('resize', place);
      viewport?.removeEventListener('scroll', place);
      viewport?.removeEventListener('resize', place);
    };
  }, [split.open]);

  const isDestructive = color === Color.Destructive;
  /** 🔒 Laid out as buttons of their own where the footer asks it (decision 3052); never a destructive split, whose entries ride its confirmation. */
  const entriesApart = useContext(SplitAsButtonsContext) && split.split && !isDestructive;
  /*
   * 🔨 Apart, the first entry leads whatever the split was armed to, so a footer narrowing to a phone never moves
   * the act a person armed on a desktop into More. The armed choice is kept and returns when the footer widens.
   */
  const armed = entriesApart ? 0 : split.armed;
  const armedLabel = entriesApart ? text[0] ?? '' : split.armedLabel;
  const onMain = () => {
    if (disabled) return;
    if (isDestructive) setDialogOpen(true);
    else if (entriesApart) actions[0]?.();
    else split.fire();
  };
  const className = [styles.button, sizeClass[size], colorClass[color], split.split && !entriesApart ? styles.split : '', disabled ? styles.disabled : '']
    .filter(Boolean)
    .join(' ');
  const panelStyle: CSSProperties | undefined = placement ? { top: placement.top, left: placement.left, width: placement.width } : undefined;
  const menu = (
    <ul
      ref={panel}
      className={styles.panel}
      role="menu"
      style={panelStyle}
      data-panel="open"
      data-placement={placement?.scheme ?? 'document'}
      data-direction={placement?.direction ?? 'below'}
    >
      {text.map((label, index) => (
        <li key={label} role="none">
          <button
            type="button"
            role="menuitem"
            className={`${styles.entry} ${index === split.armed ? styles.armed : ''}`}
            data-entry={index}
            data-armed={index === split.armed ? 'true' : 'false'}
            onClick={() => split.select(index)}
          >
            {label}
          </button>
        </li>
      ))}
    </ul>
  );

  const own = (
    <span ref={wrapper} className={className} data-color={color} data-size={size} data-split={split.split && !entriesApart ? 'true' : 'false'} data-armed={armed} {...(entriesApart ? { 'data-split-apart': 'true' } : {})}>
      <button
        type={type}
        className={`${styles.zone} ${styles.main}`}
        disabled={disabled}
        onClick={onMain}
        data-zone="main"
        {...(id === undefined ? {} : { id })}
        {...(pressed === undefined ? {} : { 'aria-pressed': pressed })}
        {...(expanded === undefined ? {} : { 'aria-expanded': expanded })}
        {...(showsReason ? { title: disabledReason, 'data-disabled-reason': disabledReason, 'aria-describedby': reasonId } : {})}
      >
        {armedLabel}
      </button>
      {/* 🔨 The reason is named to the screen reader as well as to the pointer
          (decision 0559). A `title` is read by neither a keyboard nor a reader
          reliably, and a disabled button that offers no action has nothing else
          to say why. */}
      {showsReason ? (
        <span className={styles.reason} id={reasonId}>
          {disabledReason}
        </span>
      ) : null}
      {split.split && !entriesApart ? (
        <>
          <button
            type="button"
            className={`${styles.zone} ${styles.chevron}`}
            disabled={disabled}
            aria-haspopup="menu"
            aria-expanded={split.open}
            aria-label={t('button.moreActions')}
            onClick={split.toggle}
            data-zone="chevron"
          >
            <Icon meaning="expand" size="inline" />
          </button>
          {split.open ? createPortal(menu, placement?.layer ?? document.body) : null}
        </>
      ) : null}
      {isDestructive && props.confirm ? (
        <ConfirmationDialog
          open={dialogOpen}
          title={props.confirm.title}
          consequence={props.confirm.consequence}
          confirmLabel={props.confirm.confirmLabel}
          onCancel={() => setDialogOpen(false)}
          onConfirm={() => {
            setDialogOpen(false);
            split.fire();
          }}
        />
      ) : null}
      {isDestructive && props.destructive ? (
        <DestructiveConfirmationDialog
          open={dialogOpen}
          subject={props.destructive.subject}
          phrase={props.destructive.phrase}
          toTrash={props.destructive.toTrash === true}
          onCancel={() => setDialogOpen(false)}
          onConfirm={() => {
            setDialogOpen(false);
            split.fire();
          }}
        />
      ) : null}
    </span>
  );
  if (!entriesApart) return own;
  /*
   * 🔒 Each other entry is a button of its own after this one, the same size and colour, firing its own action
   * when pressed rather than arming the main zone (decision 3052). It says why it cannot be pressed as the main
   * zone does.
   */
  const entryClass = [styles.button, sizeClass[size], colorClass[color], disabled ? styles.disabled : ''].filter(Boolean).join(' ');
  return (
    <>
      {own}
      {text.map((label, index) => (index === armed ? null : (
        <span key={`${index}-${label}`} className={entryClass} data-color={color} data-size={size} data-split="false" data-split-entry={index}>
          <button
            type="button"
            className={`${styles.zone} ${styles.main}`}
            disabled={disabled}
            onClick={() => {
              if (!disabled) actions[index]?.();
            }}
            data-zone="main"
            {...(showsReason ? { title: disabledReason, 'data-disabled-reason': disabledReason } : {})}
          >
            {label}
          </button>
        </span>
      )))}
    </>
  );
}
